import type { IAvailableOffer } from './types';

/** One plan entitlement, split so callers can tell "absent" from "present but empty". */
export type DeclaredEntitlement = { isDeclared: boolean; value: unknown };

/** Case- and whitespace-insensitive: the plan payload is authored upstream. */
export const matchesEntitlementValue = (candidate: unknown, value: string) =>
  typeof candidate === 'string' && candidate.trim().toLowerCase() === value;

/** An absent entitlement withholds nothing; a declared one must contain the value. */
export function permitsEntitlementValue(
  entitlement: DeclaredEntitlement,
  value: string,
) {
  if (!entitlement.isDeclared) return true;

  const target = value.toLowerCase();
  const { value: declared } = entitlement;

  return Array.isArray(declared)
    ? declared.some((item) => matchesEntitlementValue(item, target))
    : matchesEntitlementValue(declared, target);
}

/**
 * Pure helpers for the membership-cancellation downsell offer step.
 *
 * The selection logic is ported from identityWeb, which is the functional
 * authority: `product/product.service.ts:135-186` (`getValidProducts`) feeds
 * `my-account/cancellation-details/account-cancel-modal/account-cancel-modal.component.ts`
 * (`:210` pool sort, `:243-261` the three rungs).
 *
 * Deliberately not reusing `pages/myplan/partials/utils.tsx`'s
 * `getValidAvaliableOffers`: that implements the *upgrade* ladder (sorted by
 * `termLength`) and returns nothing for a family-to-individual downsell.
 */

/** The member's current plan, as the ladder needs to see it. */
export type CurrentPlanForLadder = {
  planCode: string;
  /** Cents. Note the ladder compares candidates' `offer.price` against this. */
  customerPrice: number;
  family: boolean;
  termName: string;
};

const PRIMARY_ALLOWED_SOURCE = 'primary';
const OFFER_CHANGE_TYPE_CUSTOM = 'custom';
const OFFER_CHANGE_TYPE_DEFAULT = 'default';

/**
 * Term names are compared case-insensitively.
 *
 * identityWeb compares raw-to-raw (`===`) because both sides come off the same
 * ngrx payload. In React the current term arrives via
 * `selectOfferSubscriptionTermName`, which **uppercases** it and defaults to
 * `MONTHLY`, while candidate offers carry the API's raw casing. A literal `===`
 * would therefore match nothing for any brand whose API returns `Annual`, and the
 * member would silently lose the retention offer they are entitled to — the same
 * class of defect BRD FR-045 exists to prevent. Normalising both sides is the safe
 * direction: it matches more offers, never fewer.
 */
function normaliseTermName(termName: string | null | undefined) {
  return termName?.trim().toLowerCase() ?? '';
}

/**
 * The candidate pool for the ladder (BRD FR-039 / BR-015 / audit note G1).
 *
 * Four operations, in this order — the order matters:
 *
 * 1. Require `offer`, `plan` and `offerChangeConfiguration` to be present, and
 *    `allowedSources` to include `primary`.
 * 2. **Set-wide** custom-over-default precedence: if *any* surviving product is
 *    `type: 'custom'`, every `type: 'default'` product is discarded. This is not a
 *    per-`toOfferCode` comparison. A product whose type is neither is dropped,
 *    matching `customProds.length > 0 ? customProds : defaultProds`.
 * 3. The `PLAN_NAME` display-name override — **applied at render, not here.**
 *    identityWeb assigns `prod.plan.name = PLAN_NAME[code]`, mutating the store
 *    object; Redux Toolkit freezes state in development, so the same write would
 *    throw. BRD FR-040 already requires the offer card to call
 *    `resolvePlanName(planCode, planName)` (`services/enrollment/utils.ts:366`),
 *    so the override lands there and this pool stays read-only. No behavioural
 *    difference: the ladder filters on plan code, price, term and family — never
 *    on the display name.
 * 4. Drop every product with a falsy `offerChangeConfiguration.toOfferCode`. An
 *    empty `toOfferCode` is the backend's deliberate "plan change blocked"
 *    sentinel, so surfacing such a plan would offer a change that can only fail.
 */
export function getValidProducts(offers: IAvailableOffer[] | null | undefined) {
  if (!Array.isArray(offers)) {
    return [];
  }

  const fromPrimarySource = offers.filter((product) => {
    const config = product?.offerChangeConfiguration;
    return (
      Boolean(product?.offer) &&
      Boolean(product?.plan) &&
      Boolean(config) &&
      Array.isArray(config.allowedSources) &&
      config.allowedSources.includes(PRIMARY_ALLOWED_SOURCE)
    );
  });

  const customProducts = fromPrimarySource.filter(
    (product) =>
      product.offerChangeConfiguration.type === OFFER_CHANGE_TYPE_CUSTOM,
  );
  const defaultProducts = fromPrimarySource.filter(
    (product) =>
      product.offerChangeConfiguration.type === OFFER_CHANGE_TYPE_DEFAULT,
  );
  const preferredProducts =
    customProducts.length > 0 ? customProducts : defaultProducts;

  return preferredProducts.filter((product) =>
    Boolean(product.offerChangeConfiguration.toOfferCode),
  );
}

/**
 * The downsell offer list (BRD FR-014 / BR-005 / audit notes G3, G5).
 *
 * The pool is sorted **price-ascending on `offer.price` before any rung runs**, so
 * all three rungs inherit that order and the cheapest candidate renders first —
 * the sort is not a rung-3-only step.
 *
 * First non-empty rung wins:
 *
 * 1. Same `planCode`, cheaper than the current plan, same term.
 * 2. Current plan is a family plan → any non-family plan on the same term,
 *    **also cheaper** — see the deviation note below.
 * 3. Current plan is an individual plan → a different `planCode`, cheaper, same
 *    term, non-family.
 *
 * Two details that look like mistakes and are not:
 *
 * - **The price test is cross-field** (BR-018): a candidate's `offer.price` is
 *   compared against the current plan's `customerPrice`, not against its `price`.
 *   Do not "correct" this to compare like fields.
 *
 * **Deviation from BRD BR-005 rung 2 and from identityWeb — requester decision,
 * 2026-08-20.** Rung 2 originally had no price test, per Angular
 * (`account-cancel-modal.component.ts:250-255`). That made a family member
 * eligible to be shown a *more expensive* same-term individual plan, and because
 * `calcSavingsPct` takes the absolute difference (FR-015's specified formula), the
 * card rendered a price increase as, for example, "Save 50%". The requester's
 * direction is that only genuinely cheaper offers are ever surfaced as a
 * downsell, so rung 2 now carries the same `isCheaperThanCurrentPlan` test as
 * rungs 1 and 3. This fixes the misleading badge at its root rather than
 * special-casing the badge.
 *
 * Residual, deliberately not addressed here: the test is on `offer.price` while
 * the badge renders `offer.customerPrice`. Where those two fields diverge (a
 * coupon, note N10) a badge could still overstate. Filtering on `customerPrice`
 * instead would break the cross-field rule BR-018 states explicitly, so the
 * divergence stays a documented open item rather than a silent second change.
 * - **Rung 3 is terminal for an individual plan.** identityWeb writes
 *   `if (downsell) return downsell`, and an array is always truthy, so an *empty*
 *   rung 3 still ends selection. Reproduced here by returning rung 3 directly.
 *
 * A consequence worth knowing: because the sort key is `offer.price` while the
 * cards display `offer.customerPrice`, cards can legitimately appear out of
 * visual price order when the two fields diverge (BR-016 / note N10).
 */
export function buildDownsellLadder(
  pool: IAvailableOffer[],
  currentPlan: CurrentPlanForLadder,
) {
  const priceAscending = [...pool].sort(
    (a, b) => a.offer.price - b.offer.price,
  );
  const currentTermName = normaliseTermName(currentPlan.termName);

  const isSameTerm = (product: IAvailableOffer) =>
    normaliseTermName(product.offer.subscriptionPlan?.termName) ===
    currentTermName;
  const isCheaperThanCurrentPlan = (product: IAvailableOffer) =>
    product.offer.price < currentPlan.customerPrice;

  const samePlanLowerPrice = priceAscending.filter(
    (product) =>
      product.plan.code === currentPlan.planCode &&
      isCheaperThanCurrentPlan(product) &&
      isSameTerm(product),
  );
  if (samePlanLowerPrice.length > 0) {
    return samePlanLowerPrice;
  }

  if (currentPlan.family) {
    const individualPlans = priceAscending.filter(
      (product) =>
        product.plan.family === false &&
        isCheaperThanCurrentPlan(product) &&
        isSameTerm(product),
    );
    if (individualPlans.length > 0) {
      return individualPlans;
    }
    return [];
  }

  return priceAscending.filter(
    (product) =>
      product.plan.code !== currentPlan.planCode &&
      isCheaperThanCurrentPlan(product) &&
      isSameTerm(product) &&
      !product.plan.family,
  );
}

/**
 * The offer card's savings badge percentage (BRD FR-015 / BR-006).
 *
 * Both sides are `customerPrice`, while the ladder filters on `offer.price`. When
 * those two fields diverge — a coupon, for instance — this can legitimately render
 * `Save 0%`. That is production behaviour and is not to be corrected (note N2).
 */
export function calcSavingsPct(
  currentCustomerPrice: number | null | undefined,
  offerCustomerPrice: number | null | undefined,
) {
  if (!currentCustomerPrice) {
    return 0;
  }
  const difference = Math.abs(currentCustomerPrice - (offerCustomerPrice ?? 0));
  return Math.round((100 * difference) / currentCustomerPrice);
}

/**
 * Normalises `plan.tier` into a `TIER_DESCRIPTIONS` lookup key (BRD FR-037 / D9).
 *
 * Returning a key rather than the description keeps this layer free of the
 * module's config, and makes the defect this requirement corrects structurally
 * impossible: identityWeb's `*ngSwitchCase="'Value' || 'Value Essential'"`
 * collapses to `'Value'`, leaving most tiers blank, whereas an object-key lookup
 * cannot substring-match — `premier complete` can never fall through to
 * `complete`. An unrecognised tier simply misses the map and renders nothing.
 */
export function resolveTierKey(tier: string | null | undefined) {
  return tier?.trim().toLowerCase() ?? '';
}
